import { css } from '@emotion/react'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin'
import { createBeautifulMentionNode } from 'lexical-beautiful-mentions'
import { isEmpty } from 'lodash-es'
import { type ComponentProps, forwardRef, useCallback, useImperativeHandle, useRef } from 'react'
import { useMount } from 'react-use'
import LexicalMentionNode, { userMentionsTheme } from './LexicalMentionNode'

import clsx from 'clsx'
import {
	$createParagraphNode,
	$createTextNode,
	$getRoot,
	type EditorState,
	type LexicalEditor,
} from 'lexical'
import LexicalMentionUserPlugin from './LexicalMentionUserPlugin'

export interface LexicalEditorHandle {
	editor: LexicalEditor | null
	clear: () => void
	getText: () => Promise<string>
	setText: (text: string) => void
}

export interface LexicalEditorProps
	extends Omit<ComponentProps<typeof ContentEditable>, 'onChange' | 'value' | 'defaultValue'> {
	onChange?: (value: string, editorState: EditorState) => void
	value?: string
	defaultValue?: string
}

export default forwardRef<LexicalEditorHandle, LexicalEditorProps>(function LexicalEditor(
	{ onChange, placeholder, defaultValue, value, className, ...props }: LexicalEditorProps,
	ref
) {
	const editorRef = useRef<LexicalEditor | null>(null)

	const getText = useCallback((): Promise<string> => {
		return new Promise<string>((resolve, reject) => {
			editorRef.current?.getEditorState().read(() => {
				resolve($getRoot().getTextContent())
			})
		})
	}, [])

	const setText = useCallback((text: string) => {
		editorRef.current?.update(() => {
			const root = $getRoot()
			root.clear()
			const textNode = $createTextNode(text)
			const paraNode = $createParagraphNode().append(textNode)
			root.append(paraNode)
			root.selectEnd()
		})
	}, [])

	const clear = useCallback(
		() =>
			new Promise<void>((resolve) => {
				setTimeout(() => {
					editorRef.current?.update(() => {
						const root = $getRoot()
						root.clear()
						resolve()
					})
				}, 0)
			}),
		[]
	)

	useImperativeHandle(
		ref,
		() => ({
			get editor() {
				return editorRef.current
			},
			getText,
			setText,
			set content(content: string) {
				const state = editorRef.current?.parseEditorState(content)
				state && editorRef.current?.setEditorState(state)
			},
			clear,
		}),
		[getText, clear, setText]
	)

	useMount(() => {
		if (isEmpty(value)) {
			return
		}

		setText(value as string)
	})

	return (
		<LexicalComposer
			initialConfig={{
				namespace: 'jike-editor',
				onError: console.error,
				nodes: [...createBeautifulMentionNode(LexicalMentionNode)],
				theme: {
					beautifulMentions: userMentionsTheme,
				},
			}}
		>
			<div
				css={css`
					position: relative;
				`}
				className={clsx(className)}
			>
				<PlainTextPlugin
					contentEditable={
						<ContentEditable
							css={css`
								width: 100%;
								appearance: none;
								border: 0;
								resize: none;
								outline: none;
								padding: unset;
								background-color: unset;

								p {
									margin: unset;
								}
							`}
							{...props}
						/>
					}
					placeholder={() => (
						<div
							css={css`
								position: absolute;
								top: 0;
								left: 0;
								color: var(--tint-tertiary);
								pointer-events: none;
							`}
						>
							{placeholder}
						</div>
					)}
					ErrorBoundary={LexicalErrorBoundary}
				/>
			</div>

			<EditorRefPlugin editorRef={editorRef} />

			<OnChangePlugin
				onChange={(state, editor) => {
					getText().then((text) => {
						onChange?.(text, state)
					})
				}}
			/>

			<LexicalMentionUserPlugin />
		</LexicalComposer>
	)
})
